import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Badge } from "../../components/ui/badge";
import {
  Search,
  Eye,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Pencil,
  ArrowDownUp,
} from "lucide-react";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Separator } from "../../components/ui/separator";
import { cn } from "../../lib/utils";
import {
  institutionsAPI,
  type Institution as ApiInstitution,
  statesAPI,
} from "../../services/api";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../contexts/AuthContext";
import { getUserInfo } from "@/lib/storeManager";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  INSTITUTION_ONBOARDING_STEPS,
  INSTITUTION_TYPES,
} from "../../constants/institutionFormFields";
import InstitutionOnboardingForm from "../../components/forms/InstitutionOnboardingForm";
import { CommonPagination } from "../../components/CommonPagination";
import { toInstitutionTitleCase } from "@/projects/Jainboarding_User/utils/institutionFormat";

interface Institution {
  id: string | number;
  name: string;
  type: string | null;
  city: string | null;
  state: string | null;
  status: string | null;
  created_at: string;
  trustee_name: string | null;
  is_highlighted?: boolean;
}

interface InstitutionStatistics {
  total_institutions: number;
  pending_approval: number;
  approved: number;
  rejected: number;
}

const mapResultToInstitution = (
  item: ApiInstitution & { approval_status?: string; is_highlighted?: boolean },
  stateMap: Record<string, string>,
  cityMap: Record<string, string>
): Institution => ({
  id: item.id,
  name: toInstitutionTitleCase(String(item.name ?? "")),
  type: item.type ?? null,
  city:
    (item as any).city_name ??
    cityMap[String((item as any).city_main)] ??
    (item as any).city_main ??
    cityMap[String(item.city)] ??
    item.city ??
    null,
  state:
    (item as any).state_name ??
    stateMap[String((item as any).state_main)] ??
    (item as any).state_main ??
    stateMap[String(item.state)] ??
    item.state ??
    null,
  status: item.approval_status ?? item.status ?? null,
  created_at: item.created_at ?? "",
  trustee_name: item.trustee_name ?? null,
  is_highlighted: item.is_highlighted ?? false,
});

/** Multi-field sort: Date → Type (“Activity”) → Name. `null` = use API list order only. */
type InstitutionSortRules = {
  date: "asc" | "desc";
  activity: "az" | "za";
  name: "az" | "za";
};

const POPOVER_SORT_DEFAULT: InstitutionSortRules = {
  date: "desc",
  activity: "az",
  name: "az",
};

function cmpAlphaLocale(a: string, b: string, aToZ: boolean): number {
  const r = (a ?? "").localeCompare(b ?? "", undefined, {
    sensitivity: "base",
  });
  return aToZ ? r : -r;
}

function compareInstitutionsComposite(
  a: Institution,
  b: Institution,
  rules: InstitutionSortRules
): number {
  const ta = new Date(a.created_at || 0).getTime();
  const tb = new Date(b.created_at || 0).getTime();
  const dateCmp = rules.date === "asc" ? ta - tb : tb - ta;
  if (dateCmp !== 0) {
    return dateCmp;
  }

  const typeCmp = cmpAlphaLocale(
    a.type ?? "",
    b.type ?? "",
    rules.activity === "az"
  );
  if (typeCmp !== 0) {
    return typeCmp;
  }

  return cmpAlphaLocale(a.name ?? "", b.name ?? "", rules.name === "az");
}

export default function Institutions() {
  const navigate = usePrefixedNavigate();
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [statistics, setStatistics] = useState<InstitutionStatistics | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);
  /** `null` = list order from API only (no `ordering` param). */
  const [appliedSortRules, setAppliedSortRules] =
    useState<InstitutionSortRules | null>(null);
  const [draftSortRules, setDraftSortRules] =
    useState<InstitutionSortRules>(POPOVER_SORT_DEFAULT);
  /** Commit sort to list + API query; `null` = server list order. Closes popover and resets to page 1. */
  const applyCommittedSort = useCallback(
    (rules: InstitutionSortRules | null) => {
      setAppliedSortRules(rules);
      setDraftSortRules(rules ? { ...rules } : { ...POPOVER_SORT_DEFAULT });
      setSortPopoverOpen(false);
      setCurrentPage(1);
    },
    []
  );
  const [highlightUpdating, setHighlightUpdating] = useState<
    string | number | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstitutionId, setEditingInstitutionId] = useState<
    string | number | null
  >(null);

  const stateMapRef = useRef<Record<string, string>>({});
  const cityMapRef = useRef<Record<string, string>>({});
  const hasFetchedStates = useRef(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPageCount, setTotalPageCount] = useState(0);

  // Institution form state (for Add/Edit modal)
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [initialData, setInitialData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [institutionImages, setInstitutionImages] = useState<File[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const [existingBrochureUrl, setExistingBrochureUrl] = useState<string>("");
  const MAX_INSTITUTION_IMAGES = 15;
  const form = useForm();

  const { user: authUser } = useAuth();
  const userInfo = getUserInfo();
  const jbfRole = authUser?.jbf_role ?? userInfo?.jbf_role ?? null;
  const isSuperAdmin = jbfRole === "super_admin";

  const handleHighlightChange = useCallback(
    async (inst: Institution, value: boolean) => {
      setHighlightUpdating(inst.id);
      try {
        await institutionsAPI.setHighlighted(inst.id, value);
        setInstitutions(prev =>
          prev.map(i =>
            i.id === inst.id ? { ...i, is_highlighted: value } : i
          )
        );
        toast.success(value ? "Institution highlighted" : "Highlight removed");
      } catch (e: any) {
        console.error("Failed to update highlight:", e);
        toast.error("Failed to update highlight");
      } finally {
        setHighlightUpdating(null);
      }
    },
    []
  );

  const fetchInstitutions = useCallback(
    async (params?: {
      search?: string;
      approval_status?: string;
      type?: string;
      ordering?: string;
      page?: number;
      page_size?: number;
    }) => {
      setLoading(true);
      try {
        if (!hasFetchedStates.current) {
          try {
            hasFetchedStates.current = true;
            const states = await statesAPI.list();
            states.forEach(s => {
              stateMapRef.current[String(s.id)] = s.name;
            });
          } catch (e) {
            console.error("Failed to fetch states:", e);
          }
        }
        const fetchParams: Record<string, string | number> = {
          page: params?.page ?? currentPage,
          page_size: params?.page_size ?? pageSize,
        };
        if (params?.search) {
          fetchParams.search = params.search;
        }
        if (params?.approval_status) {
          fetchParams.approval_status = params.approval_status;
        }
        if (params?.type) {
          fetchParams.type = params.type;
        }
        if (params?.ordering) {
          fetchParams.ordering = params.ordering;
        }
        const raw = await institutionsAPI.getApprovalsAll(fetchParams);
        // API returns PaginatedResponse<Institution>
        let list: Institution[] = [];
        let stats: InstitutionStatistics | null = null;
        let count = 0;

        if (raw && typeof raw === "object") {
          const payload = raw.data ?? raw;
          if ((payload as any).statistics) {
            stats = (payload as any).statistics;
          }
          const results =
            raw.results ?? (raw as any).data?.results ?? (raw as any).results;

          if (Array.isArray(results)) {
            const uniqueStateIds = new Set<string>();
            results.forEach((item: any) => {
              const sId = String(item.state_main ?? item.state);
              if (
                sId &&
                sId !== "null" &&
                sId !== "undefined" &&
                !isNaN(Number(sId))
              ) {
                uniqueStateIds.add(sId);
              }
            });

            // const newCityPromises = Array.from(uniqueStateIds)
            //   .filter(sId => !loadedCityStates.current.has(sId))
            //   .map(async sId => {
            //     try {
            //       const cities = await statesAPI.getCitiesByStateId(sId);
            //       cities.forEach(c => {
            //         cityMapRef.current[String(c.id)] = c.name;
            //       });
            //       loadedCityStates.current.add(sId);
            //     } catch (e) {
            //       console.error("Failed to fetch cities:", e);
            //     }
            //   });

            // await Promise.all(newCityPromises);

            list = results.map(item =>
              mapResultToInstitution(
                item,
                stateMapRef.current,
                cityMapRef.current
              )
            );
          }
          count =
            raw.count ??
            (raw as any).total_count ??
            (raw as any).data?.count ??
            list.length;
        }

        setStatistics(stats);
        setInstitutions(list);
        setTotalItems(count);
        setTotalPageCount(Math.ceil(count / (params?.page_size ?? pageSize)));
      } catch (error: any) {
        console.error("Error fetching institutions:", error);
        toast.error("Failed to fetch institutions");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const params: {
        search?: string;
        approval_status?: string;
        type?: string;
        ordering?: string;
        page: number;
        page_size: number;
      } = {
        page: currentPage,
        page_size: pageSize,
      };
      if (appliedSortRules !== null) {
        params.ordering =
          appliedSortRules.date === "desc" ? "-created_at" : "created_at";
      }
      const trimmedSearch = searchTerm.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      if (statusFilter !== "all") {
        params.approval_status = statusFilter;
      }
      if (typeFilter !== "all") {
        params.type = typeFilter;
      }
      fetchInstitutions(params);
    }, 300);

    return () => clearTimeout(timer);
  }, [
    searchTerm,
    statusFilter,
    typeFilter,
    appliedSortRules,
    currentPage,
    pageSize,
    fetchInstitutions,
  ]);

  // Load institution data when editing in modal — normalize and set all fields including Capacity & Facilities
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    if (editingInstitutionId) {
      setLoadingFormData(true);
      institutionsAPI
        .get(String(editingInstitutionId))
        .then((data: any) => {
          if (!data || typeof data !== "object") {
            return;
          }
          setInitialData(data);
          // Normalize API keys (support both snake_case and camelCase from API)
          const normalized: Record<string, unknown> = { ...data };
          if (data.total_students !== null) {
            normalized.total_students = data.total_students;
          }
          if (data.teaching_staff !== null) {
            normalized.teaching_staff = data.teaching_staff;
          }
          if (data.non_teaching_staff !== null) {
            normalized.non_teaching_staff = data.non_teaching_staff;
          }
          if (data.classrooms !== null) {
            normalized.classrooms = data.classrooms;
          }
          if (data.campus_area !== null) {
            normalized.campus_area = data.campus_area;
          }
          setFormData(normalized);
          form.reset(normalized);
          const depts = data.departments ?? data.selected_departments;
          if (Array.isArray(depts)) {
            setSelectedDepartments(depts);
          } else if (typeof depts === "string") {
            setSelectedDepartments(depts ? [depts] : []);
          } else {
            setSelectedDepartments([]);
          }
          const facs = data.facilities ?? data.selected_facilities;
          if (Array.isArray(facs)) {
            setSelectedFacilities(facs);
          } else if (typeof facs === "string") {
            setSelectedFacilities(facs ? [facs] : []);
          } else {
            setSelectedFacilities([]);
          }
          const lvls = data.levels ?? data.selected_levels;
          if (Array.isArray(lvls)) {
            setSelectedLevels(lvls);
          } else if (typeof lvls === "string") {
            setSelectedLevels(lvls ? [lvls] : []);
          }
          if (data.boarding_gender !== null) {
            form.setValue("boarding_gender", String(data.boarding_gender));
          }
          if (
            Array.isArray(data.educational_year) &&
            data.educational_year.length > 0
          ) {
            form.setValue(
              "educational_year",
              data.educational_year.map((item: any) => ({
                educational_year: String(item?.educational_year ?? ""),
                total_capacity: Number(item?.total_capacity ?? 0),
                vacant: Number(item?.vacant ?? 0),
                occupied: Number(item?.occupied ?? 0),
              }))
            );
          }
          // Existing images from API (images array of URLs) for display in edit
          const imagesArr = data.images ?? data.image;
          if (Array.isArray(imagesArr) && imagesArr.length > 0) {
            setExistingImageUrls(
              imagesArr
                .map((u: string | { url?: string }) =>
                  typeof u === "string" ? u : (u?.url ?? "")
                )
                .filter(Boolean)
            );
          } else {
            setExistingImageUrls([]);
          }
          if (data.brochure && typeof data.brochure === "string") {
            setExistingBrochureUrl(data.brochure);
          } else {
            setExistingBrochureUrl("");
          }
        })
        .catch(() => {
          toast.error("Failed to load institution data");
        })
        .finally(() => setLoadingFormData(false));
    } else {
      setStep(1);
      setFormData({});
      setInitialData(null);
      form.reset({});
      setSelectedDepartments([]);
      setSelectedFacilities([]);
      setSelectedLevels([]);
      setInstitutionImages([]);
      setExistingImageUrls([]);
      setBrochureFile(null);
      setExistingBrochureUrl("");
    }
  }, [isModalOpen, editingInstitutionId, form]);

  // Reset form state when modal closes
  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      setStep(1);
      setFormData({});
      setInitialData(null);
      form.reset({});
      setSelectedDepartments([]);
      setSelectedFacilities([]);
      setSelectedLevels([]);
      setInstitutionImages([]);
      setExistingImageUrls([]);
      setBrochureFile(null);
      setExistingBrochureUrl("");
    }
    setIsModalOpen(open);
  };

  const handleFormNext = (data: Record<string, unknown>) => {
    setFormData(prev => {
      const merged = { ...prev, ...data };
      form.reset(merged);
      return merged;
    });
    setStep(s => s + 1);
  };

  const handleFormBack = () => {
    setStep(s => s - 1);
    form.reset(formData);
  };

  const handleFormFinalSubmit = async (data: any) => {
    const rawEducationalYear =
      formData.educational_year ?? data.educational_year;
    const educational_year = Array.isArray(rawEducationalYear)
      ? rawEducationalYear.map((item: Record<string, unknown>) => ({
          educational_year: String(item?.educational_year ?? ""),
          total_capacity: Number(item?.total_capacity ?? 0),
          vacant: Number(item?.vacant ?? 0),
          occupied: Number(item?.occupied ?? 0),
        }))
      : [];
    // Merge formData + current form values (step 5) + form.getValues() so Capacity & Facilities (step 4) and all steps are in payload
    const currentFormValues = form.getValues();
    const finalData = {
      ...formData,
      ...currentFormValues,
      ...data,
      educational_year,
      selectedDepartments,
      selectedFacilities,
      selectedLevels,
      image: institutionImages,
      images: existingImageUrls,
      brochure: brochureFile ?? existingBrochureUrl ?? null,
    };

    let payloadToSubmit: Record<string, any> = finalData;

    try {
      let result: any;
      if (editingInstitutionId) {
        if (initialData) {
          payloadToSubmit = {};
          // Only pass updated field values
          for (const key of Object.keys(finalData)) {
            const val = finalData[key as keyof typeof finalData];
            let origVal = initialData[key];

            // Handle mismatched key names between frontend state and backend response for arrays
            if (key === "selectedDepartments") {
              origVal =
                initialData.departments ?? initialData.selected_departments;
            } else if (key === "selectedFacilities") {
              origVal =
                initialData.facilities ?? initialData.selected_facilities;
            } else if (key === "selectedLevels") {
              origVal = initialData.levels ?? initialData.selected_levels;
            }

            // Always pass arrays if changed to ensure proper form data mappings
            if (
              key === "educational_year" ||
              key === "selectedDepartments" ||
              key === "selectedFacilities" ||
              key === "selectedLevels" ||
              key === "image" ||
              key === "images"
            ) {
              payloadToSubmit[key] = val;
              continue;
            }

            // Send if modified string/number/boolean value
            if (String(val ?? "") !== String(origVal ?? "")) {
              payloadToSubmit[key] = val;
            }
          }
        }

        if (institutionImages.length > 0) {
          result = await institutionsAPI.updateWithImages(
            String(editingInstitutionId),
            payloadToSubmit,
            institutionImages
          );
        } else {
          result = await institutionsAPI.update(
            String(editingInstitutionId),
            payloadToSubmit
          );
        }
      } else {
        result = await institutionsAPI.create(
          payloadToSubmit,
          institutionImages
        );
      }
      const statusCode = result?.status_code;
      const isSuccess =
        statusCode === 201 ||
        statusCode === 200 ||
        Number(statusCode) === 201 ||
        Number(statusCode) === 200 ||
        (typeof result?.message === "string" &&
          /created successfully|success|updated successfully/i.test(
            result.message
          ));
      if (isSuccess) {
        toast.success(
          result?.message ??
            (editingInstitutionId
              ? "Institution updated."
              : "Institution created.")
        );
        handleModalOpenChange(false);
        fetchInstitutions();
      } else {
        toast.error(result?.message ?? "Failed to save institution");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Failed to save institution";
      toast.error(msg);
    }
  };

  const currentStepInfo = INSTITUTION_ONBOARDING_STEPS.find(
    s => s.step === step
  );

  const typeFilterOptions = useMemo(() => {
    const base = INSTITUTION_TYPES.map(t => ({
      value: t.value,
      label: t.label,
    }));
    const seen = new Set(base.map(b => b.value.toLowerCase()));
    const extras: { value: string; label: string }[] = [];
    for (const inst of institutions) {
      const t = inst.type?.trim();
      if (!t) {
        continue;
      }
      const lower = t.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        extras.push({ value: t, label: t });
      }
    }
    return [{ value: "all", label: "All types" }, ...base, ...extras];
  }, [institutions]);

  const displayInstitutions = useMemo(() => {
    if (appliedSortRules === null) {
      return institutions;
    }
    return [...institutions].sort((a, b) =>
      compareInstitutionsComposite(a, b, appliedSortRules)
    );
  }, [institutions, appliedSortRules]);

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-600 hover:bg-green-700">
            <CheckCircle className="mr-1 h-3 w-3" /> Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge variant="destructive" className="text-white">
            <XCircle className="mr-1 h-3 w-3" /> Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" /> Pending
          </Badge>
        );
    }
  };

  const institutionTableColumns = useMemo<TableColumnDef<Institution>[]>(() => {
    const cols: TableColumnDef<Institution>[] = [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 180,
        renderCell: inst => (
          <span
            className="cursor-pointer font-medium"
            onClick={() => navigate(`/super-admin/institutions/${inst.id}`)}
          >
            {inst.name}
          </span>
        ),
      },
      {
        id: "type",
        label: "Type",
        width: 100,
        cellClassName: "capitalize",
        renderCell: inst => inst.type ?? "-",
      },
      {
        id: "location",
        label: "Location",
        width: 160,
        renderCell: inst =>
          [inst.city, inst.state].filter(Boolean).join(", ") || "-",
      },
      {
        id: "trustee",
        label: "Trustee",
        width: 200,
        renderCell: inst => {
          const trustee = inst.trustee_name?.trim() ?? "-";
          return (
            <span
              className="block truncate"
              title={trustee !== "-" ? trustee : undefined}
            >
              {trustee}
            </span>
          );
        },
      },
      {
        id: "status",
        label: "Status",
        width: 120,
        renderCell: inst => getStatusBadge(inst.status),
      },
    ];
    if (isSuperAdmin) {
      cols.push({
        id: "highlighted",
        label: "Highlighted",
        width: 100,
        renderCell: inst => (
          <div onClick={e => e.stopPropagation()}>
            <Switch
              checked={Boolean(inst.is_highlighted)}
              disabled={highlightUpdating === inst.id}
              onCheckedChange={value => handleHighlightChange(inst, value)}
            />
          </div>
        ),
      });
    }
    cols.push({
      id: "actions",
      label: "Actions",
      width: 160,
      alwaysVisible: true,
      renderCell: inst => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              navigate(`/super-admin/institutions/${inst.id}`);
            }}
            title="View Details"
          >
            <Eye className="mr-1 h-4 w-4" />
            View
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-primary text-primary hover:bg-primary"
            onClick={e => {
              e.stopPropagation();
              setEditingInstitutionId(inst.id);
              setIsModalOpen(true);
            }}
            title="Edit Institution"
          >
            <Pencil className="mr-1 h-4 w-4" />
            Edit
          </Button>
        </div>
      ),
    });
    return cols;
  }, [
    currentPage,
    pageSize,
    isSuperAdmin,
    highlightUpdating,
    navigate,
    handleHighlightChange,
  ]);

  // Use API statistics when available, else compute from list
  const totalCount = statistics?.total_institutions ?? institutions.length;
  const pendingCount =
    statistics?.pending_approval ??
    institutions.filter(i => i.status === "pending").length;
  const approvedCount =
    statistics?.approved ??
    institutions.filter(i => i.status === "approved").length;
  const rejectedCount =
    statistics?.rejected ??
    institutions.filter(i => i.status === "rejected").length;

  return (
    <div className="space-y-6 overflow-x-visible overflow-y-visible p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Institution Management
          </h1>
          <p className="text-muted-foreground">
            Manage all institutions and their approvals
          </p>
        </div>
        <Button
          className="gap-2"
          onClick={() => {
            setEditingInstitutionId(null);
            setIsModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Institution
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Total Institutions
                </p>
                <p className="text-2xl font-bold">{totalCount}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Pending Approval
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  {pendingCount}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {approvedCount}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold text-red-600">
                  {rejectedCount}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters — overflow-visible so portaled sort popover is not clipped */}
      <Card className="overflow-visible">
        <CardHeader className="space-y-1 pb-2">
          <CardTitle className="text-lg font-semibold">Filters</CardTitle>
        </CardHeader>
        <CardContent className="overflow-visible pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:items-end lg:gap-x-5">
            <div className="min-w-0 space-y-1.5">
              <Label
                htmlFor="inst-filter-search"
                className="text-xs font-medium text-muted-foreground"
              >
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="inst-filter-search"
                  placeholder="Search institutions..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="h-10 border-input bg-background pl-10 shadow-sm"
                  aria-label="Search institutions"
                />
              </div>
            </div>

            <div className="min-w-0 space-y-1.5">
              <Label
                htmlFor="inst-filter-status"
                className="text-xs font-medium text-muted-foreground"
              >
                Status
              </Label>
              <Select
                value={statusFilter}
                onValueChange={v => {
                  setStatusFilter(v);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger
                  id="inst-filter-status"
                  aria-label="Filter by approval status"
                  className="h-10 w-full border-input bg-background shadow-sm"
                >
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 space-y-1.5">
              <Label
                htmlFor="inst-filter-type"
                className="text-xs font-medium text-muted-foreground"
              >
                Institution type
              </Label>
              <Select
                value={typeFilter}
                onValueChange={v => {
                  setTypeFilter(v);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger
                  id="inst-filter-type"
                  aria-label="Filter by institution type"
                  className="h-10 w-full border-input bg-background shadow-sm"
                >
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  {typeFilterOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-0 space-y-1.5 overflow-visible border-t border-border/85 pt-4 sm:col-span-2 sm:border-t-0 sm:pt-0 lg:col-span-1 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
              <Label
                htmlFor="inst-filter-sort-trigger"
                className="text-xs font-medium text-muted-foreground"
              >
                Sort by
              </Label>
              <div className="overflow-visible">
                <Popover
                  modal={false}
                  open={sortPopoverOpen}
                  onOpenChange={open => {
                    setSortPopoverOpen(open);
                    if (open) {
                      setDraftSortRules(
                        appliedSortRules ?? POPOVER_SORT_DEFAULT
                      );
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      id="inst-filter-sort-trigger"
                      type="button"
                      variant="outline"
                      aria-expanded={sortPopoverOpen}
                      aria-haspopup="dialog"
                      className={cn(
                        "h-10 w-full justify-start gap-2 rounded-md border text-sm font-normal shadow-sm transition-colors",
                        appliedSortRules !== null
                          ? "border-[#8B3232] bg-[#fdf6f6] text-[#8B3232] hover:bg-[#fcecec] hover:text-[#8B3232]"
                          : "border-input bg-background text-foreground hover:border-[#8B3232]/45 hover:bg-[#8B3232]/10 hover:text-[#8B3232]"
                      )}
                    >
                      <ArrowDownUp
                        className={cn("h-4 w-4 shrink-0 text-[#8B3232]")}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-left">
                        Sort by
                      </span>
                      {appliedSortRules !== null && (
                        <span className="ml-1 shrink-0 rounded-md bg-[#8B3232] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          Active
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    side="bottom"
                    sideOffset={8}
                    collisionPadding={16}
                    className={cn(
                      "z-[200] flex w-[min(20rem,calc(100vw-1.5rem))] flex-col gap-0 overflow-visible border border-border bg-popover p-0 text-popover-foreground shadow-xl outline-none"
                    )}
                  >
                    <div className="shrink-0 border-b border-border px-4 py-3">
                      <p className="text-base font-semibold text-foreground">
                        Sort by
                      </p>
                    </div>
                    <div className="shrink-0 space-y-4 px-4 py-4">
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          Name
                        </p>
                        <RadioGroup
                          value={draftSortRules.name}
                          onValueChange={v =>
                            setDraftSortRules(d => ({
                              ...d,
                              name: v as "az" | "za",
                            }))
                          }
                          className="grid gap-0.5"
                        >
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40",
                              draftSortRules.name === "az" && "bg-slate-100"
                            )}
                          >
                            <RadioGroupItem value="az" id="inst-sort-name-az" />
                            <span>A-Z</span>
                          </label>
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40",
                              draftSortRules.name === "za" && "bg-slate-100"
                            )}
                          >
                            <RadioGroupItem value="za" id="inst-sort-name-za" />
                            <span>Z-A</span>
                          </label>
                        </RadioGroup>
                      </div>

                      <Separator />
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          Institution type
                        </p>
                        {/* <p className="text-[11px] text-muted-foreground">
                      Institution type (A–Z / Z–A)
                    </p> */}
                        <RadioGroup
                          value={draftSortRules.activity}
                          onValueChange={v =>
                            setDraftSortRules(d => ({
                              ...d,
                              activity: v as "az" | "za",
                            }))
                          }
                          className="grid gap-0.5"
                        >
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40",
                              draftSortRules.activity === "az" && "bg-slate-100"
                            )}
                          >
                            <RadioGroupItem value="az" id="inst-sort-act-az" />
                            <span>A-Z</span>
                          </label>
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40",
                              draftSortRules.activity === "za" && "bg-slate-100"
                            )}
                          >
                            <RadioGroupItem value="za" id="inst-sort-act-za" />
                            <span>Z-A</span>
                          </label>
                        </RadioGroup>
                      </div>
                      <Separator />
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                          Date
                        </p>
                        <RadioGroup
                          value={draftSortRules.date}
                          onValueChange={v =>
                            setDraftSortRules(d => ({
                              ...d,
                              date: v as "asc" | "desc",
                            }))
                          }
                          className="grid gap-0.5"
                        >
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40",
                              draftSortRules.date === "asc" && "bg-slate-100"
                            )}
                          >
                            <RadioGroupItem
                              value="asc"
                              id="inst-sort-date-asc"
                            />
                            <span>Ascending</span>
                          </label>
                          <label
                            className={cn(
                              "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40",
                              draftSortRules.date === "desc" && "bg-slate-100"
                            )}
                          >
                            <RadioGroupItem
                              value="desc"
                              id="inst-sort-date-desc"
                            />
                            <span>Descending</span>
                          </label>
                        </RadioGroup>
                      </div>
                      {/* <button
                    type="button"
                    className="pt-1 text-xs font-medium text-[#8B3232] underline-offset-4 hover:underline"
                    onClick={() => applyCommittedSort(null)}
                  >
                    Use server list order
                  </button> */}
                    </div>
                    <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-muted/20 px-4 py-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-border"
                        onClick={() => applyCommittedSort(null)}
                      >
                        Reset
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-[#8B3232] px-4 font-semibold text-white shadow-sm hover:bg-[#743030]"
                        onClick={() =>
                          applyCommittedSort({ ...draftSortRules })
                        }
                      >
                        Apply now
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Institutions Table */}
      <Card>
        <CardContent className="p-3 pb-4">
          <DataTable
            title={`All Institutions (${totalItems})`}
            tableId="super-admin-institutions"
            columns={institutionTableColumns}
            data={displayInstitutions}
            getRowKey={inst => inst.id}
            loading={loading}
            loadingMessage="Loading institutions..."
            emptyMessage="No institutions found"
            rowClassName={() => "cursor-pointer hover:bg-gray-100"}
          />

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPageCount}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={handleModalOpenChange}>
        <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pb-2 pt-6">
            <DialogTitle>
              {editingInstitutionId
                ? "Edit Institution"
                : "Add New Institution"}
            </DialogTitle>
            <p className="pt-3 text-sm text-muted-foreground">
              Step {step} of 5 — {currentStepInfo?.title ?? ""}
            </p>
            <div className="flex gap-2 pt-1">
              {INSTITUTION_ONBOARDING_STEPS.map(s => (
                <div
                  key={s.step}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    s.step <= step ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </DialogHeader>
          <div className="relative flex flex-1 flex-col overflow-y-auto px-6">
            {loadingFormData ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
              </div>
            ) : (
              <InstitutionOnboardingForm
                step={step}
                form={form}
                onNext={handleFormNext}
                onBack={handleFormBack}
                onFinalSubmit={handleFormFinalSubmit}
                selectedDepartments={selectedDepartments}
                setSelectedDepartments={setSelectedDepartments}
                selectedFacilities={selectedFacilities}
                setSelectedFacilities={setSelectedFacilities}
                selectedLevels={selectedLevels}
                setSelectedLevels={setSelectedLevels}
                institutionImages={institutionImages}
                setInstitutionImages={setInstitutionImages}
                existingImageUrls={existingImageUrls}
                onRemoveExistingImage={index =>
                  setExistingImageUrls(prev =>
                    prev.filter((_, i) => i !== index)
                  )
                }
                brochureFile={brochureFile}
                setBrochureFile={setBrochureFile}
                existingBrochureUrl={existingBrochureUrl}
                onRemoveExistingBrochure={() => setExistingBrochureUrl("")}
                isModal
                onCancel={() => handleModalOpenChange(false)}
                maxInstitutionImages={MAX_INSTITUTION_IMAGES}
                isEditMode={!!editingInstitutionId}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
